import { type TypedDocumentNode, gql } from '@apollo/client';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons';
import { isEqual, isPast } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { LockTypeEnum } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Swap } from '@sorare/core/src/atoms/icons/Swap';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { useTimeLeft } from '@sorare/core/src/hooks/useTimeLeft';

import { LeagueDelayedEntryChip } from '../LeagueDelayedEntryChip';
import type { LeagueEntryStateChip_so5LeaderboardGroupInterface } from './__generated__/index.graphql';

const lockTypesWithLateEntry = [
  LockTypeEnum.LINEUP_CUT_OFF,
  LockTypeEnum.PLAYER_CUT_OFF,
] as LockTypeEnum[];

export const LeagueEntryStateChip = ({
  so5LeaderboardGroup,
}: {
  so5LeaderboardGroup: LeagueEntryStateChip_so5LeaderboardGroupInterface;
}) => {
  const { so5Fixture, lockType } = so5LeaderboardGroup;
  const { message } = useTimeLeft(so5Fixture?.startDate);

  const isEndDatePast = isPast(so5Fixture.endDate);

  if (
    so5LeaderboardGroup?.cutOffDate &&
    !isEqual(so5Fixture.cutOffDate, so5LeaderboardGroup.cutOffDate) &&
    !isPast(so5LeaderboardGroup.cutOffDate)
  ) {
    return <LeagueDelayedEntryChip so5LeaderboardGroup={so5LeaderboardGroup} />;
  }

  if (lockType && lockTypesWithLateEntry.includes(lockType)) {
    if (!isEndDatePast) {
      if (lockType === LockTypeEnum.LINEUP_CUT_OFF) {
        return (
          <Tooltip
            title={
              <Vertical gap={0.5}>
                <LabelS>
                  <FormattedMessage
                    id="TrckGroupAside.State.AnytimeEntry.tooltip.title"
                    defaultMessage="You can join anytime before the last match."
                  />
                </LabelS>
                <LabelS color="var(--c-nd-600)">
                  <FormattedMessage
                    id="TrckGroupAside.State.AnytimeEntry.tooltip.deadline"
                    defaultMessage="Once submitted, your lineup is locked."
                  />
                </LabelS>
              </Vertical>
            }
          >
            <Chip>
              <FontAwesomeIcon
                icon={faCalendar}
                color="var(--c-onBase-success)"
                size="xs"
              />
              <FormattedMessage
                id="TrckGroupAside.State.LateEntryAvailable"
                defaultMessage="Anytime Entry"
              />
            </Chip>
          </Tooltip>
        );
      }
      if (lockType === LockTypeEnum.PLAYER_CUT_OFF) {
        return (
          <Tooltip
            title={
              <Vertical gap={0.5}>
                <LabelS>
                  <FormattedMessage
                    id="TrckGroupAside.State.FlexibleEntry.tooltip.title"
                    defaultMessage="You can join anytime before the last match."
                  />
                </LabelS>
                <LabelS color="var(--c-nd-600)">
                  <FormattedMessage
                    id="TrckGroupAside.State.FlexibleEntry.tooltip.deadline"
                    defaultMessage="You can edit players who haven’t played yet."
                  />
                </LabelS>
              </Vertical>
            }
          >
            <Chip>
              <Swap />
              <FormattedMessage
                id="TrckGroupAside.State.FlexibleEntry"
                defaultMessage="Flexible Entry"
              />
            </Chip>
          </Tooltip>
        );
      }
    } else {
      return (
        <FormattedMessage
          id="TrckGroupAside.State.EntryClosed"
          defaultMessage="Entry Closed"
        />
      );
    }
  }
  return message;
};

LeagueEntryStateChip.fragments = {
  so5LeaderboardGroupInterface: gql`
    fragment LeagueEntryStateChip_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      cutOffDate
      canCompose {
        value
      }
      lockType
      so5Fixture {
        slug
        startDate
        endDate
        cutOffDate
      }
      ...LeagueDelayedEntryChip_so5LeaderboardGroup
    }
    ${LeagueDelayedEntryChip.fragments.so5LeaderboardGroup}
  ` as TypedDocumentNode<LeagueEntryStateChip_so5LeaderboardGroupInterface>,
};
